import React, { useState } from 'react';
import { UserManagement } from '../components/admin/UserManagement';
import { SystemMetrics } from '../components/admin/SystemMetrics';
import { CommunicationLogs } from '../components/admin/CommunicationLogs';
import { LoadingScreen } from '../components/ui/LoadingScreen';
import { Button } from '../components/ui/Button';
import { Users, BarChart2, MessageSquare } from 'lucide-react';
import { useAdminAccess } from '../hooks/admin';
import { AdminMobileNav } from '../components/admin/AdminMobileNav';
import { motion } from 'framer-motion';

type TabId = 'users' | 'metrics' | 'communications';

export const AdminPanel: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabId>('users');
  const { hasAccess, isLoading, currentUser } = useAdminAccess();

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!hasAccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600">
            You don't have permission to access the admin panel.
          </p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'users', label: 'User Management', icon: Users },
    { id: 'metrics', label: 'System Metrics', icon: BarChart2 },
    { id: 'communications', label: 'Communication Logs', icon: MessageSquare }
  ] as const;

  const renderContent = () => {
    switch (activeTab) {
      case 'users':
        return <UserManagement />;
      case 'metrics':
        return <SystemMetrics />;
      case 'communications':
        return <CommunicationLogs />;
      default:
        return <UserManagement />;
    }
  };

  return (
    <div className="max-w-7xl mx-auto space-y-8 pb-20 lg:pb-0">
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
        <div className="flex items-center gap-6 mb-6">
          <img
            src={currentUser?.avatar}
            alt={currentUser?.name}
            className="w-16 h-16 rounded-full ring-4 ring-emerald-500 ring-offset-4"
          />
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
            <p className="text-lg text-gray-600">System Management & Analytics</p>
          </div>
        </div>

        {/* Desktop Tabs */}
        <div className="hidden lg:flex gap-2">
          {tabs.map((tab) => (
            <Button
              key={tab.id}
              variant={activeTab === tab.id ? 'primary' : 'ghost'}
              onClick={() => setActiveTab(tab.id)}
              icon={<tab.icon className="w-4 h-4" />}
            >
              {tab.label}
            </Button>
          ))}
        </div>

        {/* Mobile Tab Indicator */}
        <div className="lg:hidden flex items-center justify-between mt-4 px-2">
          <h2 className="text-lg font-semibold text-gray-900">
            {tabs.find(t => t.id === activeTab)?.label}
          </h2>
        </div>
      </div>

      <motion.div
        key={activeTab}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
      >
        {renderContent()}
      </motion.div>

      {/* Mobile Navigation */}
      <AdminMobileNav activeTab={activeTab} onTabChange={setActiveTab} />
    </div>
  );
};